import DefaultTemplatePanel from "../../layout/content-templates/default/DefaultTemplatePanel.tsx";
import AssetPanelHeader from "../../common/asset-panel/AssetPanelHeader.tsx";
import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./ContentResultsPanel.module.scss";
import Accordion from "../../../../common/core-ui/Accordion.tsx";
import SearchInput from "../../../../common/core-ui/SearchInput.tsx";
import { useContentResultsFetcher } from "./searchContent.ts";
import DotLoader from "../../../../common/core-ui/DotLoader.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";

import {
  License,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import { getReaderNavLink } from "../../../bookshelfLinks.ts";
import { UnsafeHtml } from "../../../../common/core-ui/UnsafeHtml.tsx";
import { isManageAsset } from "../../../../common/features/asset/assetHelpers.ts";
import SearchResultBorrowDialog from "./SearchResultBorrowDialog.tsx";
import useAssetAvailability, {
  BorrowStatus,
} from "../../actions/explore/activate/useAssetAvailability.tsx";
import { makeSearchReturnDetail } from "./types.ts";

interface ContentResultsPanelDetailsProps {
  asset: ManageAsset | VST.Bridge.Asset;
  license?: License;
  initialQuery?: string;
  section: string;
}

type ContentResultsPanelProps = Omit<
  ComponentPropsWithoutRef<typeof DefaultTemplatePanel>,
  "children"
> &
  ContentResultsPanelDetailsProps;

function ListContent({
  fetcher,
  onCreateLink,
}: {
  fetcher: ReturnType<typeof useContentResultsFetcher>;
  onCreateLink: (deepLink: string) => {
    href?: string;
    to?: string;
    onClick?: () => void;
  };
}) {
  const intl = useIntl();
  if (fetcher.state === "loading") {
    return <DotLoader autofocus />;
  }
  if (fetcher.data && fetcher.data.sections.length === 0) {
    return (
      <div className={styles.noResults}>
        <FormattedMessage defaultMessage="No results found." />
      </div>
    );
  }

  if (fetcher.data) {
    return (
      <div className={styles.accordion}>
        {fetcher.data.sections.map((section) => (
          <Accordion
            key={section.title}
            title={section.title}
            subtitle={intl.formatMessage(
              {
                defaultMessage:
                  "{resultCount, plural, =1 {1 result} other {# results}}",
              },
              { resultCount: section.items.length }
            )}
            initiallyExpanded={true}
          >
            <ul>
              {section.items.map((item, i) => {
                return (
                  <li key={`${item.text}${i}`}>
                    <BaseLinkOrButton
                      className={styles.resultItem}
                      {...onCreateLink(item.deepLink)}
                    >
                      <UnsafeHtml className={styles.searchHitText}>
                        {item.text}
                      </UnsafeHtml>
                      <span className={styles.page}>{item.page}</span>
                    </BaseLinkOrButton>
                  </li>
                );
              })}
            </ul>
          </Accordion>
        ))}
      </div>
    );
  }
}

function ManageResultsDetailsPanel({
  fetcher,
  license,
  asset,
  initialQuery,
  section,
}: Omit<ContentResultsPanelDetailsProps, "asset"> & {
  asset: ManageAsset;
  fetcher: ReturnType<typeof useContentResultsFetcher>;
}) {
  useEffect(() => {
    if (initialQuery && fetcher.state === "idle" && !fetcher.data) {
      fetcher.submit(
        { q: initialQuery },
        { method: "GET", action: `/search/api/content/${asset.vbid}` }
      );
    }
  }, [initialQuery, fetcher, asset.vbid]);
  const onCreateLink = useCallback(
    (deepLink: string) =>
      getReaderNavLink(asset.vbid, {
        deepLink,
        returnDetail: makeSearchReturnDetail(section, asset.vbid),
      }),
    [asset.vbid, section]
  );

  return (
    <div>
      <AssetPanelHeader
        license={license}
        asset={asset}
        returnDetail={makeSearchReturnDetail(section, asset.vbid)}
      />
      <div className={styles.bottomContent}>
        <ListContent fetcher={fetcher} onCreateLink={onCreateLink} />
      </div>
    </div>
  );
}

function ExploreResultsDetailsPanel({
  fetcher,
  asset,
  initialQuery,
  section,
}: Omit<ContentResultsPanelDetailsProps, "asset"> & {
  asset: VST.Bridge.Asset;
  fetcher: ReturnType<typeof useContentResultsFetcher>;
}) {
  const intl = useIntl();
  const formRef = useRef<HTMLFormElement>(null);
  const [showBorrowDialog, setShowBorrowDialog] = useState(false);
  const { getBorrowStatus } = useAssetAvailability(asset);
  const borrowStatus = getBorrowStatus();
  const needBorrow =
    borrowStatus !== BorrowStatus.Borrowed &&
    borrowStatus !== BorrowStatus.UnlimitedCreditsAndCopies;
  const onCreateLink = useCallback(
    (deepLink: string) => {
      return needBorrow
        ? { onClick: () => setShowBorrowDialog(true) }
        : getReaderNavLink(asset.vbid, {
            needsActivation:
              BorrowStatus.UnlimitedCreditsAndCopies && !asset.active,
            deepLink,
            returnDetail: makeSearchReturnDetail(section, asset.vbid),
          });
    },
    [asset.active, asset.vbid, needBorrow, section]
  );

  useEffect(() => {
    if (
      initialQuery &&
      fetcher.state === "idle" &&
      !fetcher.data &&
      formRef.current
    ) {
      fetcher.submit(formRef.current);
    }
  }, [initialQuery, fetcher, asset.vbid]);

  return (
    <>
      <div>
        <AssetPanelHeader
          explore={asset}
          returnDetail={makeSearchReturnDetail(section, asset.vbid)}
        />
        <div className={styles.bottomContent}>
          <fetcher.Form
            method="get"
            action={`/search/api/content/${asset.vbid}`}
            ref={formRef}
          >
            <SearchInput
              name="q"
              className={styles.searchInput}
              placeholder={intl.formatMessage({
                defaultMessage: "Search Book Content",
              })}
              defaultValue={initialQuery}
            />
          </fetcher.Form>
          <ListContent onCreateLink={onCreateLink} fetcher={fetcher} />
        </div>
      </div>
      {showBorrowDialog && (
        <SearchResultBorrowDialog
          asset={asset}
          onRequestClose={() => setShowBorrowDialog(false)}
        />
      )}
    </>
  );
}

export default function ContentResultsPanel({
  asset,
  license,
  initialQuery,
  section,
  ...wrapperProps
}: ContentResultsPanelProps) {
  const fetcher = useContentResultsFetcher();
  return (
    <DefaultTemplatePanel {...wrapperProps}>
      {isManageAsset(asset) ? (
        <ManageResultsDetailsPanel
          fetcher={fetcher}
          asset={asset}
          license={license}
          initialQuery={initialQuery}
          section={section}
        />
      ) : (
        <ExploreResultsDetailsPanel
          fetcher={fetcher}
          asset={asset}
          initialQuery={initialQuery}
          section={section}
        />
      )}
    </DefaultTemplatePanel>
  );
}
